import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import BYOTEntryPoint from '@alltrails/amplitude/enums/BYOTEntryPoint';

type UploadState = {
  segmentServerIds: number[];
  waypointServerIds: number[];
};

export type ExploreCustomRouteState = {
  showSavedModalOn?: 'load' | 'save'; // Used to determine when the saved modal should be shown after navigating from the new route page to the saved route page
  isPostAuthUpsellActive?: boolean;
  uploadState?: UploadState;
  customRouteRequestIds?: Record<number, number>;
  sprigEvent?: Record<string, unknown>;
  numVisibleTopSights: number;
  scrollTopSightsIntoView: 'initiated' | 'enabled' | 'disabled';
  customRouteDetailsEntryPoint?: BYOTEntryPoint;
};

export const initialState: ExploreCustomRouteState = {
  showSavedModalOn: undefined,
  isPostAuthUpsellActive: false,
  uploadState: undefined,
  customRouteRequestIds: undefined,
  sprigEvent: undefined,
  numVisibleTopSights: 3,
  scrollTopSightsIntoView: 'disabled'
};

export const customRouteSlice = createSlice({
  name: 'exploreCustomRoute',
  initialState,
  reducers: {
    resetState: (
      state,
      action: PayloadAction<{ persistKeys?: (keyof ExploreCustomRouteState)[]; initialState?: Partial<ExploreCustomRouteState> }>
    ) => {
      const defaultState: ExploreCustomRouteState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    setShowSavedModalOn: (state, action: PayloadAction<'load' | 'save' | undefined>) => {
      state.showSavedModalOn = action.payload;
    },
    updateIsPostAuthUpsellActive: (state, action: PayloadAction<boolean>) => {
      state.isPostAuthUpsellActive = action.payload;
    },
    setUploadState: (state, action: PayloadAction<UploadState | undefined>) => {
      state.uploadState = action.payload;
    },
    incrementCustomRouteRequestId: (state, action: PayloadAction<number>) => {
      state.customRouteRequestIds ||= {};
      state.customRouteRequestIds[action.payload] ||= 0;
      state.customRouteRequestIds[action.payload]++;
    },
    setSprigEvent: (state, action: PayloadAction<Record<string, unknown> | undefined>) => {
      state.sprigEvent = action.payload;
    },
    setNumVisibleTopSights: (state, action: PayloadAction<number>) => {
      state.numVisibleTopSights = action.payload;
    },
    setScrollTopSightsIntoView: (state, action: PayloadAction<'initiated' | 'enabled' | 'disabled'>) => {
      state.scrollTopSightsIntoView = action.payload;
    },
    setCustomRouteDetailsEntryPoint: (state, action: PayloadAction<BYOTEntryPoint>) => {
      state.customRouteDetailsEntryPoint = action.payload;
    }
  }
});

export const {
  resetState,
  setShowSavedModalOn,
  updateIsPostAuthUpsellActive,
  setUploadState,
  incrementCustomRouteRequestId,
  setSprigEvent,
  setNumVisibleTopSights,
  setScrollTopSightsIntoView,
  setCustomRouteDetailsEntryPoint
} = customRouteSlice.actions;
export default customRouteSlice.reducer;
